import { StrategyMap, TacticReference, TacticOrigin, TacticMetric, TacticKSA, TacticRefDoc, TacticProcess } from '@models/index'
import { useMemo, useRef } from 'react'
import { MetricBadgeList } from '@components/shared/MetricBadge'
import { KSABadgeList } from '@components/shared/KSABadge'
import { RefDocBadgeList } from '@components/shared/RefDocBadge'
import { useAppDispatch } from '@state/hooks'
import { useTabNavigation } from '@contexts/TabNavigationContext'
import { navigateToGraphNode, navigateToMeceTactic } from '@utils/crossTabNavigation'
import './StrategyMapRenderer.css'

interface ExecutionHealthMap {
  [objectiveId: string]: 'on_track' | 'at_risk' | 'off_track'
}

interface StrategyMapRendererProps {
  data: StrategyMap | null
  onNodeClick?: (nodeId: string) => void
  showCausalArrows?: boolean  // Phase 2.1: Optional causal arrows
  selectedPillars?: Set<string>  // Phase 1.1: Selected pillars
  showCoherenceMetrics?: boolean  // Jan 2026: Show coherence metrics summary
  setWorkspaceSubTab?: (subTab: 'mece' | 'strategy-map') => void  // For MECE navigation
  executionHealth?: ExecutionHealthMap  // Phase 4: Color-code objectives by execution health
}

// =============================================================================
// NEW: Strategic Objectives Redesign (Jan 2026)
// =============================================================================
//
// Per the Implementation Plan preface:
// - Pillars ARE Strategic Objectives (the "WHY")
// - Tactics ARE Initiatives (the "HOW")
//
// The hierarchy should be:
//   PERSPECTIVE (Financial, Customer, etc.)
//     └── Strategic Objective (pillar.name, e.g., "Grow Product Sales Revenue")
//           └── Initiative (tactic.title, e.g., "Dynamic Pricing for Hearing Aids")
//
// This makes the causal logic visible to users:
//   "If we build staff expertise (L&G)... we can improve service quality (Process)...
//    which will increase customer retention (Customer)... resulting in revenue growth (Financial)"
// =============================================================================

// Strategic Objective with nested initiatives
interface StrategicObjectiveWithInitiatives {
  objective_id: string           // "F1", "C1", "P1", "L1" format
  objective_name: string         // The strategic objective (pillar name)
  description?: string           // Optional description
  ivp_priority?: boolean         // True if on IVP-identified critical path
  initiatives: InitiativeItem[]  // Tactics nested under this objective
}

// Individual initiative (tactic)
interface InitiativeItem {
  initiative_id: string
  title: string
  description: string
  tactic_refs: TacticReference[]
  tactic_origin?: TacticOrigin
  node_ids: string[]             // Supporting node IDs for evidence links
  metrics: TacticMetric[]        // KPI Grounding: Associated metrics from MEASURES relationships
  ksas: TacticKSA[]              // Competency Grounding: Associated KSAs from APPLIES_TO relationships
  ref_docs: TacticRefDoc[]       // Evidence Grounding: Associated references from ILLUSTRATES relationships
  process_docs: TacticProcess[]  // Process Intelligence: Associated process docs from DOCUMENTS relationships
}

// Perspective with nested strategic objectives
interface PerspectiveWithObjectives {
  perspective_id: string
  perspective_name: string       // "Financial", "Customer", etc.
  objectives: StrategicObjectiveWithInitiatives[]
}

/**
 * Transform pillars into the hierarchical structure:
 * Perspective > Strategic Objective > Initiatives
 *
 * This preserves the "WHY" (objectives) alongside the "HOW" (initiatives)
 */
function transformToHierarchy(data: StrategyMap): PerspectiveWithObjectives[] {
  if (!data.pillars || data.pillars.length === 0) {
    // Fallback: if no pillars, try to use perspectives directly
    if (data.perspectives && data.perspectives.length > 0) {
      return data.perspectives.map((p, idx) => ({
        perspective_id: p.perspective_id || `p${idx + 1}`,
        perspective_name: p.perspective_name,
        objectives: p.objectives.map(obj => ({
          objective_id: obj.objective_id,
          objective_name: obj.objective_name,
          description: obj.description,
          initiatives: []  // No initiatives in legacy format
        }))
      }))
    }
    return []
  }

  // Group pillars (strategic objectives) by BSC perspective
  const perspectiveGroups: Record<string, StrategicObjectiveWithInitiatives[]> = {
    'Financial': [],
    'Customer': [],
    'Internal Process': [],
    'Learning & Growth': []
  }

  data.pillars.forEach((pillar) => {
    // Detect BSC perspective from pillar metadata or content
    const perspective = pillar.bsc_perspective || detectBSCPerspective(pillar.name, pillar.description)

    // The pillar IS the strategic objective
    const strategicObjective: StrategicObjectiveWithInitiatives = {
      objective_id: pillar.objective_id || generateObjectiveId(perspective, perspectiveGroups[perspective].length + 1),
      objective_name: pillar.name,  // THIS is the strategic objective name
      description: pillar.description,
      ivp_priority: pillar.ivp_priority || false,
      // Tactics ARE the initiatives nested under this objective
      initiatives: (pillar.tactics || []).map(tactic => ({
        initiative_id: tactic.initiative_id || tactic.tactic_id || '',
        title: tactic.title,
        description: tactic.description || '',
        tactic_refs: tactic.tactic_refs || [],
        tactic_origin: tactic.tactic_origin as TacticOrigin,
        node_ids: extractNodeIdsFromTactic(tactic),
        metrics: tactic.metrics || [],  // KPI Grounding: Carry metrics through
        ksas: tactic.ksas || [],  // Competency Grounding: Carry KSAs through
        ref_docs: tactic.ref_docs || [],  // Evidence Grounding: Carry ref_docs through
        process_docs: tactic.process_docs || []  // Process Intelligence: Carry process_docs through
      }))
    }

    perspectiveGroups[perspective].push(strategicObjective)
  })

  // Convert to array format, maintaining BSC display order (Financial -> Customer -> Process -> L&G)
  const bscOrder = ['Financial', 'Customer', 'Internal Process', 'Learning & Growth']

  return bscOrder
    .filter(perspectiveName => perspectiveGroups[perspectiveName].length > 0)
    .map((perspectiveName, idx) => ({
      perspective_id: `p${idx + 1}`,
      perspective_name: perspectiveName,
      objectives: perspectiveGroups[perspectiveName]
    }))
}

/**
 * Generate objective ID in BSC format (F1, C1, P1, L1)
 */
function generateObjectiveId(perspective: string, index: number): string {
  const prefixes: Record<string, string> = {
    'Financial': 'F',
    'Customer': 'C',
    'Internal Process': 'P',
    'Learning & Growth': 'L'
  }
  return `${prefixes[perspective] || 'X'}${index}`
}

/**
 * Detect BSC perspective from objective/pillar content
 */
function detectBSCPerspective(name: string, description?: string): string {
  const combined = ((name || '') + ' ' + (description || '')).toLowerCase()

  if (combined.includes('financial') || combined.includes('revenue') || combined.includes('profit') || combined.includes('cost') || combined.includes('margin')) {
    return 'Financial'
  }
  if (combined.includes('customer') || combined.includes('client') || combined.includes('satisfaction') || combined.includes('market') || combined.includes('retention')) {
    return 'Customer'
  }
  if (combined.includes('learning') || combined.includes('growth') || combined.includes('talent') || combined.includes('training') || combined.includes('capability') || combined.includes('skill')) {
    return 'Learning & Growth'
  }
  return 'Internal Process'
}

/**
 * Extract node IDs from a tactic for evidence links
 */
function extractNodeIdsFromTactic(tactic: any): string[] {
  const nodeIds: string[] = []

  if (tactic.node_ids) nodeIds.push(...tactic.node_ids)
  if (tactic.supporting_node_ids) nodeIds.push(...tactic.supporting_node_ids)
  if (tactic.data_citations) {
    tactic.data_citations.forEach((cit: any) => {
      if (typeof cit === 'string' && cit.endsWith('_id')) nodeIds.push(cit)
      else if (cit?.node_id) nodeIds.push(cit.node_id)
    })
  }

  return [...new Set(nodeIds)]
}

// Phase 5: Safe text display with fallback
function safeText(text: any, fallback: string = ''): string {
  if (typeof text === 'string' && text.trim().length > 0) {
    return text.trim()
  }
  return fallback
}

// Phase 5: Get BSC perspective icon
function getBSCIcon(perspective: string): string {
  const icons: Record<string, string> = {
    'Financial': '$',
    'Customer': '@',
    'Internal Process': '#',
    'Learning & Growth': '*'
  }
  return icons[perspective] || '#'
}

// Jan 2026: Get tactic origin display info with tooltip explanations
function getTacticOriginDisplay(origin?: TacticOrigin): { label: string; className: string; tooltip: string } | null {
  if (!origin || origin === 'mece') return null  // Don't show badge for MECE origin

  const displays: Record<string, { label: string; className: string; tooltip: string }> = {
    'rescue': {
      label: 'Enabler',
      className: 'origin-rescue',
      tooltip: 'This initiative was identified from graph analysis as a supporting enabler. It was not part of the original MECE table but is important for strategy execution.'
    },
    'augmented': {
      label: 'AI-Generated',
      className: 'origin-augmented',
      tooltip: 'This initiative was generated by AI to fill a strategic gap in BSC coverage.'
    },
    'template': {
      label: 'Template',
      className: 'origin-template',
      tooltip: 'This initiative was derived from a strategic template.'
    }
  }
  return displays[origin] || null
}

export function StrategyMapRenderer({
  data,
  onNodeClick,
  showCausalArrows: _showCausalArrows = true,  // Phase 2.1: Reserved for future causal arrows feature
  selectedPillars: _selectedPillars,  // Phase 1.1: Selected pillars (reserved for future filtering)
  showCoherenceMetrics = true,  // Jan 2026: Show coherence metrics by default
  setWorkspaceSubTab,  // For MECE navigation
  executionHealth,  // Phase 4: Execution health overlay
}: StrategyMapRendererProps) {
  const dispatch = useAppDispatch()
  const { navigateToTab } = useTabNavigation()

  // Phase 2.1: Container ref for positioning
  const containerRef = useRef<HTMLDivElement>(null)

  // Handler to navigate to a node in Business Graph
  const handleNavigateToNode = (nodeId: string) => {
    navigateToGraphNode(nodeId, navigateToTab, dispatch)
    onNodeClick?.(nodeId)
  }

  // Handler to navigate to a MECE tactic (Jan 2026)
  const handleNavigateToMeceTactic = (tacticRef: string) => {
    navigateToMeceTactic(tacticRef, navigateToTab, setWorkspaceSubTab)
  }

  // Transform data to hierarchical structure: Perspective > Objective > Initiative
  const perspectives = useMemo(() => {
    if (!data) return []
    return transformToHierarchy(data)
  }, [data])

  // Handle missing or invalid data
  if (perspectives.length === 0) {
    return (
      <div className="strategy-map-renderer">
        <p className="no-data-message">No strategy map data available.</p>
      </div>
    )
  }

  // Count totals for summary stats
  const totalObjectives = perspectives.reduce((sum, p) => sum + p.objectives.length, 0)
  const totalInitiatives = perspectives.reduce(
    (sum, p) => sum + p.objectives.reduce((oSum, o) => oSum + o.initiatives.length, 0),
    0
  )

  return (
    <div className="strategy-map-renderer" ref={containerRef} style={{ position: 'relative' }}>
      {/* Summary stats */}
      <div className="strategy-map-stats" style={{ position: 'relative', zIndex: 5 }}>
        <span className="stat-item">{perspectives.length} perspective{perspectives.length !== 1 ? 's' : ''}</span>
        <span className="stat-separator">|</span>
        <span className="stat-item">{totalObjectives} strategic objective{totalObjectives !== 1 ? 's' : ''}</span>
        <span className="stat-separator">|</span>
        <span className="stat-item">{totalInitiatives} initiative{totalInitiatives !== 1 ? 's' : ''}</span>
      </div>

      {/* Jan 2026: Coherence Metrics Summary */}
      {showCoherenceMetrics && data?.coherence_metrics && (
        <div className="coherence-metrics-summary" style={{ position: 'relative', zIndex: 5 }}>
          <div className="coherence-metric">
            <span className={`metric-value ${data.coherence_metrics.overall_coherence_score >= 90 ? 'good' : data.coherence_metrics.overall_coherence_score >= 70 ? 'warning' : 'poor'}`}>
              {data.coherence_metrics.overall_coherence_score}%
            </span>
            <span className="metric-label">Strategic Coherence</span>
          </div>
          <div className="coherence-metric">
            <span className="metric-value">{data.coherence_metrics.tactic_coverage_pct}%</span>
            <span className="metric-label">Initiative Coverage</span>
            <span className="metric-detail">
              ({data.coherence_metrics.referenced_tactics}/{data.coherence_metrics.total_mece_tactics})
            </span>
          </div>
          <div className="coherence-metric">
            <span className="metric-value">{data.coherence_metrics.initiative_grounding_pct}%</span>
            <span className="metric-label">Initiative Grounding</span>
            <span className="metric-detail">
              ({data.coherence_metrics.grounded_initiatives}/{data.coherence_metrics.mece_origin_initiatives})
            </span>
          </div>
        </div>
      )}

      {/* Main hierarchy: Perspective > Strategic Objective > Initiatives */}
      {perspectives.map(perspective => (
        <div
          key={perspective.perspective_id}
          className="perspective-section"
          data-perspective={perspective.perspective_name}
          style={{ position: 'relative', zIndex: 5 }}
        >
          {/* Perspective Header */}
          <h4 className="perspective-name">
            <span className="perspective-icon">{getBSCIcon(perspective.perspective_name)}</span>
            {perspective.perspective_name}
          </h4>

          {/* Strategic Objectives (nested under perspective) */}
          <div className="objectives-container">
            {perspective.objectives.length === 0 ? (
              <p className="no-objectives-message">No strategic objectives defined for this perspective.</p>
            ) : perspective.objectives.map(objective => (
              <div key={objective.objective_id} className={`strategic-objective-section${objective.ivp_priority ? ' ivp-critical-path' : ''}${executionHealth?.[objective.objective_id] ? ` health-${executionHealth[objective.objective_id]}` : ''}`}>
                {/* Strategic Objective Header - THE "WHY" */}
                <div className="strategic-objective-header">
                  <span className="objective-icon">&#127919;</span>
                  <h5 className="strategic-objective-name">
                    {safeText(objective.objective_name, 'Untitled Strategic Objective')}
                  </h5>
                  <span className="objective-id-badge">{objective.objective_id}</span>
                  <span className="initiative-count">
                    ({objective.initiatives.length} initiative{objective.initiatives.length !== 1 ? 's' : ''})
                  </span>
                </div>

                {/* Optional objective description */}
                {objective.description && (
                  <p className="strategic-objective-description">{objective.description}</p>
                )}

                {/* KPI Metrics for this objective (deduplicated across initiatives) */}
                {(() => {
                  const seen = new Set<string>()
                  const objMetrics = objective.initiatives.flatMap(
                    (init) => init.metrics || []
                  ).filter((m) => {
                    if (seen.has(m.id)) return false
                    seen.add(m.id)
                    return true
                  })
                  return objMetrics.length > 0 ? (
                    <MetricBadgeList metrics={objMetrics} compact={true} />
                  ) : null
                })()}

                {/* KSA Competencies for this objective (deduplicated across initiatives) */}
                {(() => {
                  const seen = new Set<string>()
                  const objKSAs = objective.initiatives.flatMap(
                    (init) => init.ksas || []
                  ).filter((k) => {
                    if (seen.has(k.id)) return false
                    seen.add(k.id)
                    return true
                  })
                  return objKSAs.length > 0 ? (
                    <KSABadgeList ksas={objKSAs} compact={true} />
                  ) : null
                })()}

                {/* Reference Documents at objective level (deduplicated across initiatives) */}
                {(() => {
                  const seen = new Set<string>()
                  const objRefs = objective.initiatives.flatMap(
                    (init: any) => init.ref_docs || []
                  ).filter((r: any) => {
                    if (seen.has(r.id)) return false
                    seen.add(r.id)
                    return true
                  })
                  return objRefs.length > 0 ? (
                    <RefDocBadgeList refDocs={objRefs} compact={true} />
                  ) : null
                })()}

                {/* Initiatives nested under this objective - THE "HOW" */}
                <div className="initiatives-list">
                  {objective.initiatives.length === 0 ? (
                    <p className="no-initiatives-message">No initiatives mapped to this objective.</p>
                  ) : objective.initiatives.map((initiative, idx) => {
                    const originDisplay = getTacticOriginDisplay(initiative.tactic_origin)
                    const hasNodeLinks = initiative.node_ids.length > 0

                    return (
                      <div
                        key={initiative.initiative_id || idx}
                        className="initiative-item"
                        data-initiative-id={initiative.initiative_id}
                      >
                        <div className="initiative-header">
                          <span className="initiative-name">
                            {safeText(initiative.title, 'Untitled Initiative')}
                          </span>

                          {/* MECE Tactic Reference Badges */}
                          {initiative.tactic_refs.length > 0 && (
                            <div className="tactic-refs-badges">
                              <span className="refs-label">MECE:</span>
                              {initiative.tactic_refs.map((tacticRef, refIdx) => (
                                <button
                                  key={refIdx}
                                  className="tactic-ref-badge"
                                  onClick={() => handleNavigateToMeceTactic(tacticRef.ref)}
                                  title={`${tacticRef.ref}: ${tacticRef.title}\nPillar: ${tacticRef.pillar_name}`}
                                >
                                  {tacticRef.ref}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Origin Badge for non-MECE initiatives */}
                          {originDisplay && (
                            <span className={`origin-badge ${originDisplay.className}`} title={originDisplay.tooltip}>
                              {originDisplay.label}
                            </span>
                          )}
                        </div>

                        {initiative.description && (
                          <p className="initiative-description">
                            {safeText(initiative.description, '')}
                          </p>
                        )}

                        {/* Evidence links to Business Graph */}
                        {hasNodeLinks && (
                          <div className="initiative-nav-links">
                            <span className="nav-label">Evidence:</span>
                            {initiative.node_ids.slice(0, 3).map((nodeId, nodeIdx) => (
                              <button
                                key={nodeIdx}
                                className="btn-nav-node"
                                onClick={() => handleNavigateToNode(nodeId)}
                                title={`View ${nodeId} in Business Graph`}
                                aria-label={`Navigate to ${nodeId} in Business Graph`}
                              >
                                {nodeId.replace(/_id$/, '').replace(/_/g, ' ')}
                              </button>
                            ))}
                            {initiative.node_ids.length > 3 && (
                              <span className="more-nodes">+{initiative.node_ids.length - 3} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Phase 2.1: Causal arrows overlay - DISABLED: adds negligible value */}
      {/* TODO: Implement improved visualization approach */}
    </div>
  )
}
